import { motion } from 'framer-motion';
import { useTheme } from '@/contexts/ThemeContext';

export default function ThemeToggle({ useWhiteText = true, compact = false }) {
  const { isDark, toggleTheme, isTransitioning } = useTheme();

  // Dark mode: standard dark button
  // Light mode + white text context (landing top): transparent, white icon
  // Light mode + dark text context (scrolled / other pages): transparent, amber icon
  const buttonCls = isDark
    ? 'bg-white/5 border-white/20 hover:bg-white/10 hover:border-white/30'
    : 'bg-transparent border-transparent hover:bg-black/5';

  const sunCls = isDark
    ? 'opacity-0 scale-50'
    : useWhiteText
      ? 'opacity-100 scale-100 text-white'
      : 'opacity-100 scale-100 text-amber-500';

  return (
    <button
      onClick={toggleTheme}
      disabled={isTransitioning}
      className={`relative flex items-center justify-center rounded-none border transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
        compact ? "w-8 h-8" : "w-12 h-12"
      } ${buttonCls}`}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      data-testid="theme-toggle"
    >
      <motion.div
        animate={{ rotate: isDark ? 0 : 180 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        className={`relative ${compact ? "w-4 h-4" : "w-6 h-6"}`}
      >
        {/* Sun Icon */}
        <motion.svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`absolute inset-0 ${compact ? "w-4 h-4" : "w-6 h-6"} transition-all duration-300 ${sunCls}`}
        >
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </motion.svg>

        {/* Moon Icon */}
        <motion.svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`absolute inset-0 ${compact ? "w-4 h-4" : "w-6 h-6"} transition-all duration-300 ${
            isDark ? 'opacity-100 scale-100 text-blue-400' : 'opacity-0 scale-50'
          }`}
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </motion.svg>
      </motion.div>
    </button>
  );
}
